import React, { Fragment } from "react";
import clsx from "clsx";

import { Grid, makeStyles } from "@material-ui/core";
import { useFormState } from "react-final-form";
import Link from "@material-ui/core/Link";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import DownloadFile from "../../../../components/DownloadFile/DownloadFile";
import { ReactComponent as RequiredDocumentsInsideCircle } from "../../../../assets/svg/required-documents-inside-circle.svg";
import { ReactComponent as ApplicationLink } from "../../../../assets/svg/application-links.svg";
import { ReactComponent as SupportingDocuments } from "../../../../assets/svg/supporting-documents.svg";
import { ReactComponent as Comment } from "../../../../assets/svg/comment.svg";
import { ReactComponent as ApplicationFiles } from "../../../../assets/svg/application-files.svg";
import { ReactComponent as Intakes } from "../../../../assets/svg/calendar-yellow.svg";
import { ReactComponent as Branch } from "../../../../assets/svg/branch-location.svg";
import { ReactComponent as StudentAvatar } from "../../../../assets/svg/student-avatar.svg";
import { EducationType } from "../../../school/constants/types";

const useStyles = makeStyles(() => ({
  card: {
    wordBreak: "break-word",
    backgroundColor: "#f5f6f7",
    borderRadius: 12,
    padding: "11px 16px",
    color: "#000",
    fontWeight: "normal",
    fontSize: 16,
    "& + &": {
      marginTop: 16,
    },
    minHeight: 88,
  },
  label: {
    color: "#383d4a",
    fontWeight: "bold",
    fontSize: 18,
    marginBottom: 8,
    display: "flex",
    "& > span": {
      marginLeft: 8,
      paddingTop: 4,
    },
  },
  link: {
    fontSize: 16,
    color: "#7D9EF1",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "none",
      color: "#5E88F1",
    },
  },
  linkRejected: {
    color: "#F94B40",
  },
  linkConfirmed: {
    color: "black",
    fontWeight: "bold",
  },
  reqDocTitle: {
    "& ::first-letter": {
      textTransform: "uppercase",
    },
  },
  uploadFileContainer: {
    display: "flex",
    flexDirection: "column",
    "& > div > div > div": {
      padding: 0,
    },
  },
  icon: {
    width: 32,
    height: 32,
  },
  appLink: {
    marginBottom: 0,
    marginTop: 8,
  },
}));

const ViewApplication = () => {
  const classes = useStyles();

  const { values } = useFormState();

  const applicationForms = values.injectedData.filledForms;
  const applicationLink =
    values.injectedData.educationType === EducationType.Language
      ? values.injectedData.course.applicationLink
      : values.injectedData.program.academic.applicationLink;
  const requiredDocs = values.injectedData.requiredDocs;
  const extraDocs = values.injectedData.extraDocs;
  const isLinkConfirmed = values.step1.isLinkConfirmed;

  return (
    <>
      <div className={classes.card}>
        <div className={classes.label}>
          <StudentAvatar className={classes.icon} />
          <span>Student</span>
        </div>
        {values.injectedData.student.name}
      </div>
      <div className={classes.card}>
        <div className={classes.label}>
          <Branch className={classes.icon} />
          <span>Branch</span>
        </div>
        {values.injectedData.branch.name}
      </div>
      {!!values.selectedIntake && (
        <div className={classes.card}>
          <div className={classes.label}>
            <Intakes className={classes.icon} />
            <span>Intake</span>
          </div>
          {values.selectedIntake.name}
        </div>
      )}
      {!!applicationForms.length && (
        <div className={classes.card}>
          <div className={classes.label}>
            <ApplicationFiles className={classes.icon} />
            <span>Application Files</span>
          </div>
          <Grid container spacing={2}>
            {applicationForms.map((appForm) => (
              <Fragment key={appForm.received.id}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  className={classes.uploadFileContainer}
                >
                  <DownloadFile needBorder={false} file={appForm.received} />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  className={classes.uploadFileContainer}
                >
                  <UploadFile
                    isViewMode
                    uploadedFiles={appForm.filled ? [appForm.filled.file] : []}
                  />
                </Grid>
              </Fragment>
            ))}
          </Grid>
        </div>
      )}
      {!!applicationLink && (
        <div className={classes.card}>
          <div className={classes.label}>
            <ApplicationLink className={classes.icon} />
            <span>Application Link</span>
          </div>
          <Link href={applicationLink} className={classes.link} target="_blank">
            Click here
          </Link>
          <p
            className={clsx(
              isLinkConfirmed ? classes.linkConfirmed : classes.linkRejected,
              classes.appLink,
            )}
          >
            {isLinkConfirmed ? "Confirmed" : "Not confirmed"}
          </p>
        </div>
      )}
      {!!requiredDocs.length && (
        <div className={classes.card}>
          <div className={classes.label}>
            <RequiredDocumentsInsideCircle className={classes.icon} />
            <span>Required Documents</span>
          </div>
          <Grid container spacing={2}>
            {requiredDocs.map((reqDoc) => (
              <Grid
                item
                xs={12}
                sm={6}
                key={reqDoc.received.id}
                className={classes.uploadFileContainer}
              >
                <span className={classes.reqDocTitle}>
                  {reqDoc.received.name}
                </span>
                <UploadFile
                  isViewMode
                  uploadedFiles={reqDoc.filled ? [reqDoc.filled.file] : []}
                />
              </Grid>
            ))}
          </Grid>
        </div>
      )}
      {!!extraDocs.length && (
        <div className={classes.card}>
          <div className={classes.label}>
            <SupportingDocuments className={classes.icon} />
            <span>Supporting Documents</span>
          </div>
          <Grid container spacing={2}>
            {extraDocs.map((receivedRequiredDoc) => (
              <Grid
                item
                xs={12}
                sm={6}
                key={receivedRequiredDoc.id}
                className={classes.uploadFileContainer}
              >
                <UploadFile isViewMode uploadedFiles={[receivedRequiredDoc]} />
              </Grid>
            ))}
          </Grid>
        </div>
      )}
      {!!values.injectedData.comment && (
        <div className={classes.card}>
          <div className={classes.label}>
            <Comment className={classes.icon} />
            <span>Comment</span>
          </div>
          {values.injectedData.comment}
        </div>
      )}
    </>
  );
};

export default ViewApplication;
